import React, { useState, useEffect } from 'react';
import { ShieldCheckIcon, InfoIcon, ChevronDownIcon, UploadIcon, ImageIcon, XIcon } from 'lucide-react';

const INDUSTRIES = [
  { value: 'trades_construction',   label: 'Trades & Construction' },
  { value: 'manufacturing',         label: 'Manufacturing' },
  { value: 'professional_services', label: 'Professional Services' },
  { value: 'retail',                label: 'Retail' },
  { value: 'technology',            label: 'Technology' },
];

const PROVINCES = [
  'Ontario', 'British Columbia', 'Alberta', 'Quebec', 'Manitoba',
  'Saskatchewan', 'Nova Scotia', 'New Brunswick',
  'Newfoundland and Labrador', 'Prince Edward Island',
];

const DEAL_PREFERENCES = [
  'Full Sale', 'Partial Sale', 'Management Buyout',
  'Seller Willing to Finance', 'Open to Earnout',
  'Owner Transition Support',
];

const TIMELINES = [
  { value: '6_12_months', label: '6–12 Months' },
  { value: '1_2_years',   label: '1–2 Years' },
  { value: '3_plus_years', label: '3+ Years' },
];

const STEP_LABELS = ['BUSINESS BASICS', 'ASKING PRICE', 'YOUR STORY', 'PHOTOS', 'REVIEW & PUBLISH'];
const TOTAL_STEPS = STEP_LABELS.length;
const PHOTO_MAX_COUNT = 10;
const PHOTO_MAX_BYTES = 5 * 1024 * 1024;
const ALLOWED_PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

const formatCurrency = (value) => {
  const num = Number(value) || 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD',
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(num);
};

const stripDigits = (value) => String(value || '').replace(/[^0-9]/g, '');

export default function DealRoomListingForm({
  mode = 'new',
  initialListing = null,
  saving = false,
  serverErrors = [],
  persona = 'SELLER',
  userName = '',
  userInitials = '',
  planProgress = { completed: 0, total: 5, percentage: 0 },
  actionPlanBadge = 0,
  onSave, // ({ id, industry, ..., photo_signed_ids })
  onCancel,
  uploadPhoto, // (File) => Promise<signed_id>
  // { fields, sources, newest_source_dated } — null in edit mode or
  // when the user has no Succeedo snapshots yet. Drives the "Use my
  // data" banner above Step 1 and the per-field "Pre-filled" pills.
  draftSuggestions = null,
}) {
  const isEdit = mode === 'edit';
  const [step, setStep] = useState(1);
  const [clientErrors, setClientErrors] = useState([]);

  // Banner state machine: 'pending' (showing) → 'applied' (user clicked
  // Use my data) | 'dismissed' (user clicked Start fresh). Hidden when
  // there are no suggestions to offer.
  const hasSuggestions = !isEdit
    && draftSuggestions
    && draftSuggestions.fields
    && Object.keys(draftSuggestions.fields).length > 0
    // The default `timeline` alone isn't worth a banner — only show
    // when at least one substantive field came back.
    && Object.keys(draftSuggestions.fields).some((k) => k !== 'timeline');

  // bannerStatus is 'hidden' on first mount (Apollo still loading) and
  // flips to 'pending' once suggestions land. After the user clicks
  // Use-my-data ('applied') or Start-fresh ('dismissed') the status
  // sticks — we only auto-flip on the initial null→data transition.
  const [bannerStatus, setBannerStatus] = useState('hidden');
  useEffect(() => {
    setBannerStatus((prev) => {
      if (prev !== 'hidden') return prev;
      return hasSuggestions ? 'pending' : 'hidden';
    });
  }, [hasSuggestions]);

  // prefilledFields tracks which keys came from suggestions and have
  // not yet been edited by the user. Drives the "Pre-filled · Edit"
  // pills next to each input. Cleared per-field when the user types.
  const [prefilledFields, setPrefilledFields] = useState(new Set());

  const [data, setData] = useState({
    industry:           initialListing?.industry           || 'trades_construction',
    province:           initialListing?.province           || 'Ontario',
    years_in_operation: initialListing?.years_in_operation != null ? String(initialListing.years_in_operation) : '',
    employees:          initialListing?.employees != null  ? String(initialListing.employees) : '',
    revenue:            initialListing?.revenue != null    ? String(initialListing.revenue) : '',
    adjusted_ebitda:    initialListing?.adjusted_ebitda != null ? String(initialListing.adjusted_ebitda) : '',
    asking_price:       initialListing?.asking_price != null ? String(initialListing.asking_price) : '',
    deal_preferences:   initialListing?.deal_preferences   || ['Full Sale'],
    timeline:           initialListing?.timeline           || '1_2_years',
    business_story:     initialListing?.business_story     || '',
    key_strengths:      initialListing?.key_strengths      || '',
  });

  // Photos are tracked outside the main `data` object because they go
  // through their own upload pipeline (multipart -> signed_id) rather
  // than the typed wizard inputs. Each entry is { id, url, signedId,
  // uploading?, error? }. id is local-only; signedId is what we ship to
  // SaveListing. Existing photos in edit mode have signedId === null
  // because we don't round-trip signed_ids on read — we re-attach
  // everything in the array on save, so an existing photo in the array
  // means "keep it". Removing one from the array means "purge it".
  const [photos, setPhotos] = useState(() => {
    const urls = initialListing?.photo_urls || [];
    const signedIds = initialListing?.photo_signed_ids || [];
    return urls.map((url, i) => ({
      id: `existing-${i}`,
      url,
      signedId: signedIds[i] || null,
      existing: true,
    }));
  });
  const [photoUploadError, setPhotoUploadError] = useState('');
  const [photosTouched, setPhotosTouched] = useState(false);

  // Wrapping update() to clear "Pre-filled" pills when the user edits
  // a previously suggested field. Treats deal_preferences as a single
  // logical field (any toggle clears the pill, even if the array still
  // contains other suggested values).
  const clearPrefilledFor = (keys) => {
    setPrefilledFields((prev) => {
      if (prev.size === 0) return prev;
      const next = new Set(prev);
      keys.forEach((k) => next.delete(k));
      return next;
    });
  };

  const update = (patch) => {
    setData((d) => ({ ...d, ...patch }));
    clearPrefilledFor(Object.keys(patch));
  };
  const togglePref = (pref) => {
    update({
      deal_preferences: data.deal_preferences.includes(pref)
        ? data.deal_preferences.filter((p) => p !== pref)
        : [...data.deal_preferences, pref],
    });
  };

  // Apply the suggestion bundle into form state when the seller clicks
  // "Use my data". Only fields that came back populated are touched.
  const applySuggestions = () => {
    if (!draftSuggestions?.fields) return;
    const f = draftSuggestions.fields;
    const patch = {};
    if (f.industry)                      patch.industry           = f.industry;
    if (f.province)                      patch.province           = f.province;
    if (f.years_in_operation != null)    patch.years_in_operation = String(f.years_in_operation);
    if (f.employees != null)             patch.employees          = String(f.employees);
    if (f.revenue != null)               patch.revenue            = String(f.revenue);
    if (f.adjusted_ebitda != null)       patch.adjusted_ebitda    = String(f.adjusted_ebitda);
    if (f.asking_price != null)          patch.asking_price       = String(f.asking_price);
    if (Array.isArray(f.deal_preferences) && f.deal_preferences.length) patch.deal_preferences = f.deal_preferences;
    if (f.timeline)                      patch.timeline           = f.timeline;
    setData((d) => ({ ...d, ...patch }));
    setPrefilledFields(new Set(Object.keys(patch)));
    setBannerStatus('applied');
  };

  const ebitdaNum    = Number(data.adjusted_ebitda) || 0;
  const suggestedMin = Math.floor(ebitdaNum * 1.6);
  const suggestedMax = Math.floor(ebitdaNum * 2.2);
  const progress     = (step / TOTAL_STEPS) * 100;

  const handleFileChange = async (e) => {
    setPhotoUploadError('');
    const files = Array.from(e.target.files || []);
    e.target.value = ''; // allow re-selecting the same file later

    const remainingSlots = PHOTO_MAX_COUNT - photos.length;
    if (remainingSlots <= 0) {
      setPhotoUploadError(`You can only upload up to ${PHOTO_MAX_COUNT} photos.`);
      return;
    }

    const accepted = files.slice(0, remainingSlots).filter((f) => {
      if (!ALLOWED_PHOTO_TYPES.includes(f.type)) {
        setPhotoUploadError('Photos must be JPEG, PNG, or WebP.');
        return false;
      }
      if (f.size > PHOTO_MAX_BYTES) {
        setPhotoUploadError('Photos must be smaller than 5MB.');
        return false;
      }
      return true;
    });

    // Sequential uploads — avoids hammering the upload endpoint when
    // a user drops 10 photos at once. Browser-side dedupe of the file
    // input keeps this from being a UX bottleneck in practice.
    /* eslint-disable no-await-in-loop */
    for (const file of accepted) {
      const tempId = `upload-${Date.now()}-${Math.random()}`;
      const previewUrl = URL.createObjectURL(file);
      setPhotos((prev) => [...prev, { id: tempId, url: previewUrl, signedId: null, uploading: true }]);
      try {
        if (typeof uploadPhoto !== 'function') throw new Error('Upload not configured');
        const signedId = await uploadPhoto(file);
        setPhotos((prev) => prev.map((p) => (p.id === tempId ? { ...p, signedId, uploading: false } : p)));
        setPhotosTouched(true);
      } catch (err) {
        setPhotoUploadError(err.message || 'Upload failed.');
        setPhotos((prev) => prev.filter((p) => p.id !== tempId));
        URL.revokeObjectURL(previewUrl);
      }
    }
    /* eslint-enable no-await-in-loop */
  };

  const handleRemovePhoto = (id) => {
    setPhotos((prev) => {
      const removed = prev.find((p) => p.id === id);
      if (removed?.url?.startsWith('blob:')) URL.revokeObjectURL(removed.url);
      return prev.filter((p) => p.id !== id);
    });
    setPhotosTouched(true);
  };

  const isUploadingPhoto = photos.some((p) => p.uploading);

  const validateStep = (s) => {
    const errs = [];
    if (s >= 1) {
      if (!data.industry) errs.push('Industry is required');
      if (!data.province) errs.push('Province is required');
      if (!data.years_in_operation) errs.push('Years in operation is required');
      if (!data.employees) errs.push('Number of employees is required');
      if (!data.revenue) errs.push('Annual revenue is required');
      if (!data.adjusted_ebitda) errs.push('Adjusted EBITDA is required');
    }
    if (s >= 2) {
      if (!data.asking_price) errs.push('Asking price is required');
      if (data.deal_preferences.length === 0) errs.push('Pick at least one deal preference');
      if (!data.timeline) errs.push('Timeline is required');
    }
    return errs;
  };

  const handleNext = () => {
    const errs = validateStep(step);
    if (errs.length) { setClientErrors(errs); return; }
    setClientErrors([]);
    if (step < TOTAL_STEPS) {
      setStep(step + 1);
    } else {
      // Only ship photo_signed_ids if the user actually touched the photos
      // step. Otherwise omit the arg so SaveListing leaves existing photos
      // untouched (it purges-then-attaches when the arg is present).
      const photoIds = photosTouched ? photos.filter((p) => p.signedId).map((p) => p.signedId) : undefined;

      onSave?.({
        id: initialListing?.id,
        industry: data.industry,
        province: data.province,
        years_in_operation: Number(data.years_in_operation),
        employees: Number(data.employees),
        revenue: Number(data.revenue),
        adjusted_ebitda: Number(data.adjusted_ebitda),
        asking_price: Number(data.asking_price),
        deal_preferences: data.deal_preferences,
        timeline: data.timeline,
        business_story: data.business_story,
        key_strengths: data.key_strengths,
        photo_signed_ids: photoIds,
      });
    }
  };

  const handleBack = () => { setClientErrors([]); if (step > 1) setStep(step - 1); };

  const allErrors = [...clientErrors, ...serverErrors, ...(photoUploadError ? [photoUploadError] : [])];

  const stepTitle = {
    1: 'Tell us about your business',
    2: 'Set your asking price and deal terms',
    3: 'Tell your business story',
    4: 'Add photos to your listing',
    5: 'Review your confidential listing',
  }[step];

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12">
      <div className="bg-card border border-border rounded-[16px] p-8 max-w-[680px] w-full">
        <div className="mb-8">
          <div className="text-[11px] font-mono uppercase text-muted-foreground tracking-wide mb-3">
            STEP {step} OF {TOTAL_STEPS} — {STEP_LABELS[step - 1]}
          </div>
          <div className="h-1.5 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[hsl(var(--succeedo-orange))] to-[hsl(var(--succeedo-orange))]/70 transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        <h2 className="font-serif text-[32px] font-light text-foreground tracking-[-0.02em] mb-6">
          {stepTitle}
        </h2>

        {allErrors.length > 0 && (
          <div className="bg-destructive/10 border border-destructive/30 rounded-[12px] p-4 mb-6">
            <ul className="text-sm text-destructive space-y-1">
              {allErrors.map((err, i) => <li key={i}>{err}</li>)}
            </ul>
          </div>
        )}

        {step === 1 && bannerStatus === 'pending' && (
          <UseMyDataBanner
            suggestions={draftSuggestions}
            onApply={applySuggestions}
            onDismiss={() => setBannerStatus('dismissed')}
          />
        )}

        {step === 1 && (
          <Step1 data={data} update={update} prefilled={prefilledFields} sources={draftSuggestions?.sources} />
        )}
        {step === 2 && (
          <Step2
            data={data}
            update={update}
            togglePref={togglePref}
            suggestedMin={suggestedMin}
            suggestedMax={suggestedMax}
            prefilled={prefilledFields}
            sources={draftSuggestions?.sources}
          />
        )}
        {step === 3 && (
          <Step3 data={data} update={update} />
        )}
        {step === 4 && (
          <Step4Photos
            photos={photos}
            onFileChange={handleFileChange}
            onRemovePhoto={handleRemovePhoto}
          />
        )}
        {step === 5 && (
          <Step5Review data={data} photos={photos} />
        )}

        <div className="flex items-center justify-between mt-8">
          {step > 1 ? (
            <button
              type="button"
              onClick={handleBack}
              className="text-[14px] text-muted-foreground hover:text-foreground transition-colors"
            >
              ← Back
            </button>
          ) : onCancel ? (
            <button
              type="button"
              onClick={onCancel}
              className="text-[14px] text-muted-foreground hover:text-foreground transition-colors"
            >
              Cancel
            </button>
          ) : <span />}
          <button
            type="button"
            onClick={handleNext}
            disabled={saving || isUploadingPhoto}
            className={`bg-foreground text-background rounded-full px-6 py-[10px] text-[14px] font-medium hover:bg-foreground/90 transition-colors disabled:opacity-50 ${step === 1 && !onCancel ? 'w-full' : ''}`}
          >
            {step === 5
              ? (saving ? 'Publishing…' : 'Publish My Listing')
              : step === 4
                ? (photos.length > 0 ? 'Next: Review & Publish →' : 'Skip photos for now →')
                : step === 3
                  ? 'Next: Photos →'
                  : step === 2
                    ? 'Next: Your Story →'
                    : 'Next: Asking Price →'}
          </button>
        </div>

        {step === 5 && (
          <p className="text-[10px] font-mono uppercase text-muted-foreground/60 text-center mt-3 tracking-wide">
            LISTING GOES LIVE ANONYMOUSLY — PARTNER BROKER NOTIFIED
          </p>
        )}
      </div>
    </div>
  );
}

function ConfidentialityNotice() {
  return (
    <div className="bg-[hsl(var(--succeedo-orange))]/5 border border-[hsl(var(--succeedo-orange))]/20 rounded-[12px] p-4 mb-6 flex gap-3">
      <ShieldCheckIcon className="w-4 h-4 text-[hsl(var(--succeedo-orange))] flex-shrink-0 mt-0.5" />
      <p className="text-[13px] text-muted-foreground">
        Your business name and identity are never shown to buyers until you approve an NDA.
      </p>
    </div>
  );
}

// One-time banner above Step 1 surfacing snapshot-derived suggestions.
// Hidden once the user clicks Use my data or Start fresh.
function UseMyDataBanner({ suggestions, onApply, onDismiss }) {
  const dated = suggestions?.newest_source_dated;
  const monthLabel = (() => {
    if (!dated) return null;
    try {
      const d = new Date(dated);
      return d.toLocaleString('en-US', { month: 'long' });
    } catch (_e) {
      return null;
    }
  })();

  // Pull a short list of the source tools that contributed, for the
  // copy. e.g. "your March valuation and April deal-structure work".
  const sourceTypes = new Set(Object.values(suggestions?.sources || {}).map((s) => s.type));
  const toolBits = [];
  if (sourceTypes.has('valuation_snapshot')) toolBits.push('valuation');
  if (sourceTypes.has('deal_structure_snapshot')) toolBits.push('deal-structure work');
  const toolPhrase = toolBits.length === 0
    ? 'Succeedo data'
    : toolBits.length === 1
      ? toolBits[0]
      : `${toolBits.slice(0, -1).join(', ')} and ${toolBits[toolBits.length - 1]}`;

  return (
    <div className="bg-[hsl(var(--succeedo-orange))]/10 border border-[hsl(var(--succeedo-orange))]/30 rounded-[12px] p-5 mb-6">
      <div className="font-mono text-[11px] uppercase tracking-wide text-[hsl(var(--succeedo-orange))] mb-2">
        WE FOUND YOUR {monthLabel ? `${monthLabel.toUpperCase()} ` : ''}{toolPhrase.toUpperCase()}
      </div>
      <p className="text-[14px] text-foreground mb-4">
        Pre-fill industry, revenue, EBITDA, structure, and timeline from your existing {toolPhrase}? You can edit anything before publishing.
      </p>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onApply}
          className="bg-[hsl(var(--succeedo-orange))] text-white rounded-full px-5 py-[9px] text-sm hover:opacity-90 transition-opacity"
        >
          Use my data
        </button>
        <button
          type="button"
          onClick={onDismiss}
          className="border border-border text-foreground rounded-full px-5 py-[9px] text-sm hover:bg-accent transition-colors"
        >
          Start fresh
        </button>
      </div>
    </div>
  );
}

// Per-input attribution pill. Shown when prefilledFields includes the
// field key. Source-aware copy ("from your valuation").
function PrefilledPill({ source }) {
  const label = (() => {
    switch (source?.type) {
      case 'valuation_snapshot':      return 'from your valuation';
      case 'deal_structure_snapshot': return 'from your deal-structure work';
      case 'user_profile':            return 'from your profile';
      default:                        return 'pre-filled';
    }
  })();

  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-[hsl(var(--succeedo-orange))]/10 text-[hsl(var(--succeedo-orange))] text-[10px] font-mono uppercase tracking-wide px-2 py-0.5 ml-2">
      <ShieldCheckIcon className="w-3 h-3" />
      {label}
    </span>
  );
}

function Step1({ data, update, prefilled, sources }) {
  const isPre = (k) => prefilled?.has(k);
  const src = (k) => sources?.[k];
  return (
    <div className="space-y-6">
      <ConfidentialityNotice />
      <SelectField
        label="Industry"
        value={data.industry}
        onChange={(v) => update({ industry: v })}
        options={INDUSTRIES}
        labelExtra={isPre('industry') && <PrefilledPill source={src('industry')} />}
      />
      <SelectField
        label="Province"
        value={data.province}
        onChange={(v) => update({ province: v })}
        options={PROVINCES.map((p) => ({ value: p, label: p }))}
        labelExtra={isPre('province') && <PrefilledPill source={src('province')} />}
      />
      <div className="grid grid-cols-2 gap-4">
        <NumberField
          label="Years in Operation"
          value={data.years_in_operation}
          onChange={(v) => update({ years_in_operation: stripDigits(v) })}
          labelExtra={isPre('years_in_operation') && <PrefilledPill source={src('years_in_operation')} />}
        />
        <NumberField
          label="Number of Employees"
          value={data.employees}
          onChange={(v) => update({ employees: stripDigits(v) })}
          labelExtra={isPre('employees') && <PrefilledPill source={src('employees')} />}
        />
      </div>
      <CurrencyField
        label="Annual Revenue"
        value={data.revenue}
        onChange={(v) => update({ revenue: stripDigits(v) })}
        placeholder="2,100,000"
        labelExtra={isPre('revenue') && <PrefilledPill source={src('revenue')} />}
      />
      <CurrencyField
        label="Adjusted EBITDA"
        value={data.adjusted_ebitda}
        onChange={(v) => update({ adjusted_ebitda: stripDigits(v) })}
        placeholder="420,000"
        tooltip="This is your normalized profit"
        labelExtra={isPre('adjusted_ebitda') && <PrefilledPill source={src('adjusted_ebitda')} />}
      />
    </div>
  );
}

function Step2({ data, update, togglePref, suggestedMin, suggestedMax, prefilled, sources }) {
  const isPre = (k) => prefilled?.has(k);
  const src = (k) => sources?.[k];

  // C — when the asking_price came from DealStructureSnapshot, surface
  // the seller's own number explicitly above the EBITDA range. Both
  // lines render together; the seller can still freely edit.
  const askSource = src('asking_price');
  const askFromDealStructure = askSource?.type === 'deal_structure_snapshot';
  const askMonthLabel = (() => {
    if (!askSource?.dated) return null;
    try { return new Date(askSource.dated).toLocaleString('en-US', { month: 'long' }); } catch (_e) { return null; }
  })();

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-[13px] text-muted-foreground mb-2">
          Asking Price
          {isPre('asking_price') && <PrefilledPill source={askSource} />}
        </label>
        <div className="relative mb-3">
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground text-[20px]">$</span>
          <input
            type="text"
            value={data.asking_price}
            onChange={(e) => update({ asking_price: stripDigits(e.target.value) })}
            placeholder="1,400,000"
            className="w-full bg-muted/50 border border-border rounded-[8px] pl-10 pr-4 py-4 text-foreground text-[20px] focus:outline-none focus:border-[hsl(var(--succeedo-orange))] transition-colors"
          />
        </div>
        {(Number(data.adjusted_ebitda) > 0 || askFromDealStructure) && (
          <div className="bg-[hsl(var(--succeedo-orange))]/5 border border-[hsl(var(--succeedo-orange))]/20 rounded-[8px] p-3 space-y-1">
            {askFromDealStructure && (
              <p className="text-[11px] font-mono text-foreground">
                Your {askMonthLabel ? `${askMonthLabel} ` : ''}deal-structure work suggested {formatCurrency(data.asking_price)}
              </p>
            )}
            {Number(data.adjusted_ebitda) > 0 && (
              <p className="text-[11px] font-mono text-muted-foreground">
                Range based on EBITDA × 1.6–2.2: {formatCurrency(suggestedMin)} – {formatCurrency(suggestedMax)}
              </p>
            )}
          </div>
        )}
      </div>

      <div>
        <label className="block text-[13px] text-muted-foreground mb-3">
          Deal Preferences
          {isPre('deal_preferences') && <PrefilledPill source={src('deal_preferences')} />}
        </label>
        <div className="flex flex-wrap gap-2">
          {DEAL_PREFERENCES.map((pref) => {
            const selected = data.deal_preferences.includes(pref);
            return (
              <button
                key={pref}
                type="button"
                onClick={() => togglePref(pref)}
                className={`rounded-full px-4 py-2 text-[13px] border transition-colors ${
                  selected ? 'bg-foreground text-background border-foreground' : 'bg-muted/50 text-foreground border-border hover:border-foreground/40'
                }`}
              >
                {pref}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <label className="block text-[13px] text-muted-foreground mb-3">
          Timeline
          {isPre('timeline') && <PrefilledPill source={src('timeline')} />}
        </label>
        <div className="flex flex-wrap gap-2">
          {TIMELINES.map(({ value, label }) => {
            const selected = data.timeline === value;
            return (
              <button
                key={value}
                type="button"
                onClick={() => update({ timeline: value })}
                className={`rounded-full px-4 py-2 text-[13px] border transition-colors ${
                  selected ? 'bg-foreground text-background border-foreground' : 'bg-muted/50 text-foreground border-border hover:border-foreground/40'
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function Step3({ data, update }) {
  return (
    <div className="space-y-6">
      <p className="text-[14px] text-muted-foreground">
        Help buyers understand what makes your business special. This information remains confidential until you approve an NDA.
      </p>
      <CharCountTextarea
        label="Business Story"
        value={data.business_story}
        onChange={(v) => update({ business_story: v.slice(0, 1000) })}
        rows={6}
        placeholder="Describe your business, its history, and what makes it unique..."
      />
      <CharCountTextarea
        label="Key Strengths & Opportunities"
        value={data.key_strengths}
        onChange={(v) => update({ key_strengths: v.slice(0, 1000) })}
        rows={6}
        placeholder="What are the key strengths of your business? What opportunities exist for growth?"
      />
    </div>
  );
}

function Step4Photos({ photos, onFileChange, onRemovePhoto }) {
  return (
    <div className="space-y-6">
      <div className="bg-[hsl(var(--succeedo-orange))]/5 border border-[hsl(var(--succeedo-orange))]/20 rounded-[12px] p-4 flex gap-3">
        <ShieldCheckIcon className="w-4 h-4 text-[hsl(var(--succeedo-orange))] flex-shrink-0 mt-0.5" />
        <p className="text-[13px] text-muted-foreground">
          Add photos to bring your listing to life. They stay blurred to buyers until they sign an NDA.
        </p>
      </div>

      {photos.length < PHOTO_MAX_COUNT && (
        <label className="block border-2 border-dashed border-border rounded-[12px] p-8 text-center cursor-pointer hover:border-[hsl(var(--succeedo-orange))] hover:bg-[hsl(var(--succeedo-orange))]/5 transition-colors">
          <input
            type="file"
            accept={ALLOWED_PHOTO_TYPES.join(',')}
            multiple
            onChange={onFileChange}
            className="hidden"
          />
          <UploadIcon className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
          <p className="text-[14px] text-foreground font-medium mb-1">
            Drop photos here or click to browse
          </p>
          <p className="text-[12px] text-muted-foreground">
            JPEG, PNG, or WebP — up to 5MB each. Maximum {PHOTO_MAX_COUNT} photos.
          </p>
        </label>
      )}

      {photos.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <label className="text-[13px] text-muted-foreground">
              Your Photos ({photos.length}/{PHOTO_MAX_COUNT})
            </label>
            <p className="text-[11px] text-muted-foreground">First photo is your cover image</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {photos.map((photo, index) => (
              <div
                key={photo.id}
                className="relative bg-muted border border-border rounded-[12px] overflow-hidden aspect-[4/3]"
              >
                <img src={photo.url} alt="" className="w-full h-full object-cover" />

                {index === 0 && (
                  <span className="absolute top-2 left-2 bg-[hsl(var(--succeedo-orange))]/90 text-white text-[10px] font-mono uppercase px-2 py-0.5 rounded">
                    COVER
                  </span>
                )}

                {photo.uploading && (
                  <div className="absolute inset-0 bg-foreground/40 flex items-center justify-center">
                    <span className="text-white text-[12px]">Uploading…</span>
                  </div>
                )}

                {!photo.uploading && (
                  <button
                    type="button"
                    onClick={() => onRemovePhoto(photo.id)}
                    className="absolute top-2 right-2 bg-foreground/70 hover:bg-destructive text-white rounded-full w-7 h-7 flex items-center justify-center transition-colors"
                    aria-label="Remove photo"
                  >
                    <XIcon className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {photos.length === 0 && (
        <div className="text-center py-6">
          <ImageIcon className="w-12 h-12 text-muted-foreground/40 mx-auto mb-3" />
          <p className="text-[13px] text-muted-foreground">
            No photos yet. You can publish without — and add them anytime later.
          </p>
        </div>
      )}
    </div>
  );
}

function Step5Review({ data, photos }) {
  const industryLabel = INDUSTRIES.find((i) => i.value === data.industry)?.label || data.industry;
  const timelineLabel = TIMELINES.find((t) => t.value === data.timeline)?.label || data.timeline;
  const structure = data.deal_preferences.join(' + ') || '—';

  return (
    <div className="space-y-6">
      <div className="bg-muted/50 border border-border rounded-[16px] p-6">
        <div className="text-[11px] font-mono uppercase text-muted-foreground tracking-wide mb-4">BUSINESS</div>
        <dl className="space-y-3">
          <ReviewRow label="Industry"  value={industryLabel} />
          <ReviewRow label="Province"  value={data.province} />
          <ReviewRow label="Revenue"   value={data.revenue ? formatCurrency(data.revenue) : '—'} />
          <ReviewRow label="EBITDA"    value={data.adjusted_ebitda ? formatCurrency(data.adjusted_ebitda) : '—'} />
          <ReviewRow label="Employees" value={data.employees || '—'} />
        </dl>

        <div className="h-px bg-border my-6" />

        <div className="text-[11px] font-mono uppercase text-muted-foreground tracking-wide mb-4">DEAL TERMS</div>
        <dl className="space-y-3">
          <ReviewRow label="Asking Price" value={data.asking_price ? formatCurrency(data.asking_price) : '—'} />
          <ReviewRow label="Structure"    value={structure} />
          <ReviewRow label="Timeline"     value={timelineLabel} />
        </dl>

        {photos.length > 0 && (
          <>
            <div className="h-px bg-border my-6" />
            <div className="text-[11px] font-mono uppercase text-muted-foreground tracking-wide mb-3">
              PHOTOS ({photos.length})
            </div>
            <div className="grid grid-cols-3 gap-2">
              {photos.slice(0, 6).map((p) => (
                <img key={p.id} src={p.url} alt="" className="aspect-[4/3] object-cover rounded-[8px] bg-muted" />
              ))}
            </div>
          </>
        )}
      </div>

      <div className="flex items-center gap-2 text-muted-foreground">
        <ShieldCheckIcon className="w-3.5 h-3.5 text-[hsl(var(--chart-2))]" />
        <span className="text-[13px]">Name and contact details hidden until NDA approved.</span>
      </div>
    </div>
  );
}

function ReviewRow({ label, value }) {
  return (
    <div className="flex justify-between">
      <dt className="text-[13px] text-muted-foreground">{label}</dt>
      <dd className="text-[14px] text-foreground">{value}</dd>
    </div>
  );
}

function SelectField({ label, value, onChange, options, labelExtra }) {
  return (
    <div>
      <label className="block text-[13px] text-muted-foreground mb-2">
        {label}
        {labelExtra}
      </label>
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-muted/50 border border-border rounded-[8px] px-4 py-3 text-foreground appearance-none focus:outline-none focus:border-[hsl(var(--succeedo-orange))] transition-colors"
        >
          {options.map(({ value: v, label: l }) => <option key={v} value={v}>{l}</option>)}
        </select>
        <ChevronDownIcon className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
      </div>
    </div>
  );
}

function NumberField({ label, value, onChange, labelExtra }) {
  return (
    <div>
      <label className="block text-[13px] text-muted-foreground mb-2">
        {label}
        {labelExtra}
      </label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-muted/50 border border-border rounded-[8px] px-4 py-3 text-foreground focus:outline-none focus:border-[hsl(var(--succeedo-orange))] transition-colors"
      />
    </div>
  );
}

function CurrencyField({ label, value, onChange, placeholder, tooltip, labelExtra }) {
  return (
    <div>
      <label className="block text-[13px] text-muted-foreground mb-2 flex items-center gap-2">
        {label}
        {tooltip && (
          <span className="group relative inline-flex">
            <InfoIcon className="w-3.5 h-3.5 text-muted-foreground/60 cursor-help" />
            <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block bg-foreground text-background text-xs rounded px-2 py-1 whitespace-nowrap z-10">
              {tooltip}
            </span>
          </span>
        )}
        {labelExtra}
      </label>
      <div className="relative">
        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full bg-muted/50 border border-border rounded-[8px] pl-8 pr-4 py-3 text-foreground focus:outline-none focus:border-[hsl(var(--succeedo-orange))] transition-colors"
        />
      </div>
    </div>
  );
}

function CharCountTextarea({ label, value, onChange, rows, placeholder }) {
  return (
    <div>
      <label className="block text-[13px] text-muted-foreground mb-2">{label}</label>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        placeholder={placeholder}
        className="w-full bg-muted/50 border border-border rounded-[8px] px-4 py-3 text-foreground focus:outline-none focus:border-[hsl(var(--succeedo-orange))] transition-colors resize-none"
      />
      <p className="text-[11px] text-muted-foreground mt-2">{value.length} / 1000 characters</p>
    </div>
  );
}
